<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div class="max-w-md w-full text-center">
      <h1 class="text-6xl font-bold text-gray-900 dark:text-white mb-4">{{ statusCode }}</h1>
      <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ title }}</h2>
      <p class="text-gray-500 dark:text-gray-400 mb-8">{{ message }}</p>
      <div class="flex justify-center gap-4">
        <!-- Reload only makes sense for transient errors (5xx). On 404
             the page won't reappear; Go Home is the better answer.
             Hide the reload affordance for 404 to avoid confusion. -->
        <button
          v-if="statusCode !== 404"
          @click="reloadPage"
          class="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
        >Reload page</button>
        <NuxtLink to="/" class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
          Go to dashboard
        </NuxtLink>
      </div>

      <!-- Debug info in development -->
      <div v-if="isDev && error" class="mt-8 text-left bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
        <p class="text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  error: Object
})

const statusCode = computed(() => props.error?.statusCode || 500)

const title = computed(() => {
  const titles: Record<number, string> = {
    404: 'Page Not Found',
    401: 'Unauthorized',
    403: 'Forbidden',
    429: 'Too Many Requests',
    500: 'Server Error',
    503: 'Service Unavailable'
  }
  return titles[statusCode.value] || 'Error'
})

const message = computed(() => {
  return props.error?.statusMessage || props.error?.message || 'Something went wrong. Please try again.'
})

const isDev = process.dev

// "Reload page" actually reloads the same URL — useful for transient
// 5xx errors. The previous "Try Again" called clearError({ redirect:
// '/' }) which is what "Go to dashboard" does, making the two buttons
// identical and confusing.
function reloadPage() {
  if (typeof window !== 'undefined') window.location.reload()
}
</script>
